<template>
    <div ref="viewerRoot" class="allergens-pdf-viewer" :class="[$mq]">
        <div class="file-name" :title="fileName">{{ fileName }}</div>

        <AllergensStatusMessage
            v-if="hasError"
            :title="$t('allergens.error.title')"
            :description="$t('allergens.error.description')"
        />

        <div v-else class="preview-pdf">
            <div class="pdf-header">
                <div class="pages">
                    <button
                        type="button"
                        class="btn prev"
                        :class="{ disabled: pageNum <= 1 || isLoading }"
                        :aria-label="$t('pdfjs.controls.prev')"
                        @click="onPrevPage"
                    />
                    <span class="number">
                        <span class="pdf-page-num">{{ pageNum }}</span> /
                        <span class="pdf-page-count">{{ numPages || '∞' }}</span>
                    </span>
                    <button
                        type="button"
                        class="btn next"
                        :class="{ disabled: isLoading || !numPages || pageNum >= numPages }"
                        :aria-label="$t('pdfjs.controls.next')"
                        @click="onNextPage"
                    />
                </div>

                <div class="zoom">
                    <button
                        type="button"
                        class="btn zoom-out"
                        :class="{ disabled: isLoading || !canZoomOut }"
                        :aria-label="$t('pdfjs.controls.zoomout')"
                        @click="onZoomOut"
                    />
                    <button
                        type="button"
                        class="btn zoom-in"
                        :class="{ disabled: isLoading || !canZoomIn }"
                        :aria-label="$t('pdfjs.controls.zoomin')"
                        @click="onZoomIn"
                    />
                    <button
                        type="button"
                        class="btn fullscreen"
                        :aria-label="$t('pdfjs.controls.fullscreen')"
                        @click="onFullscreen"
                    />
                </div>
            </div>

            <div ref="pdfPages" class="pdf-pages scrollbar">
                <div v-if="isLoading" class="pdf-loading">
                    <Skeleton shape="rounded" width="100%" height="100%" />
                </div>
                <canvas v-show="!isLoading" :id="canvasId"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import { Api } from '@/core/api.js'
import AllergensStatusMessage from '@/components/domain/allergens/AllergensStatusMessage'

const api = new Api()

// El 100% equivale al documento ajustado al ancho del visor.
const MIN_ZOOM = 50
const MAX_ZOOM = 300
const ZOOM_STEP = 10

export default {
    name: 'AllergensPdfViewer',
    components: {
        AllergensStatusMessage
    },
    props: {
        cardId: {
            type: [String, Number],
            required: true,
            validator(value) {
                return value !== null && value !== undefined && String(value) !== ''
            }
        },
        fileName: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            canvasId: 'canvas-allergens-pdf-' + this._uid,
            pdfjsLib: null,
            pdfDoc: null,
            pageNum: 1,
            numPages: 0,
            pageRendering: false,
            pageNumPending: null,
            canvas: null,
            ctx: null,
            zoom: 100,
            isLoading: false,
            hasError: false,
            loadToken: 0,
            resizeTimer: null
        }
    },
    computed: {
        normalizedCardId() {
            return String(this.cardId)
        },
        pdfLibraryLoaded() {
            return this.$store.getters['library/getPdfLibrary']
        },
        canZoomIn() {
            return this.zoom < MAX_ZOOM
        },
        canZoomOut() {
            return this.zoom > MIN_ZOOM
        }
    },
    watch: {
        cardId() {
            this.loadPDFLib()
        }
    },
    mounted() {
        this.loadPDFLib()
        window.addEventListener('resize', this.onWindowResize)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize)
        clearTimeout(this.resizeTimer)
        this.destroyPdf()
    },
    methods: {
        destroyPdf() {
            if (this.pdfDoc && typeof this.pdfDoc.destroy === 'function') {
                this.pdfDoc.destroy()
            }
            this.pdfDoc = null
            this.pageNum = 1
            this.numPages = 0
            this.pageNumPending = null
            this.pageRendering = false
            this.canvas = null
            this.ctx = null
        },
        getPdfScriptBase() {
            // History mode: pathname es /allergens y rompería la ruta relativa.
            return this.$store.getters.getIsApp ? '' : '/'
        },
        loadPDFLib() {
            if (!this.normalizedCardId) {
                return
            }

            const self = this
            const base = this.getPdfScriptBase()
            this.isLoading = true

            if (!this.pdfLibraryLoaded || !window['pdfjs-dist/build/pdf']) {
                const script = document.createElement('script')
                script.setAttribute('src', base + 'js/pdf.min.js?' + process.env.VUE_APP_VERSION)
                script.onload = function () {
                    self.pdfjsLib = window['pdfjs-dist/build/pdf']
                    self.pdfjsLib.GlobalWorkerOptions.workerSrc = base + 'js/pdf.worker.js?' + process.env.VUE_APP_VERSION
                    self.$store.commit('library/setPdfLibrary', true)
                    self.getPdf()
                }
                script.onerror = function () {
                    self.destroyPdf()
                    self.isLoading = false
                    self.hasError = true
                }
                document.head.appendChild(script)
                return
            }

            this.pdfjsLib = window['pdfjs-dist/build/pdf']
            this.pdfjsLib.GlobalWorkerOptions.workerSrc = base + 'js/pdf.worker.js?' + process.env.VUE_APP_VERSION
            this.getPdf()
        },
        async getPdf() {
            if (!this.pdfjsLib || !this.normalizedCardId) {
                return
            }

            const loadToken = ++this.loadToken
            this.isLoading = true
            this.hasError = false
            this.destroyPdf()

            try {
                // El fichero original está en un dominio sin CORS, por lo que se sirve
                // a través de la API con la sesión del usuario.
                const pdfDoc = await this.pdfjsLib.getDocument({
                    url: api.getBaseUrl() + 'allergens/' + this.normalizedCardId + '/download?action=get&platform=' + api.getPlatform(),
                    httpHeaders: { Authorization: await api.getAuthToken() },
                    withCredentials: true
                }).promise

                if (loadToken !== this.loadToken) {
                    pdfDoc.destroy()
                    return
                }

                this.pdfDoc = pdfDoc
                this.zoom = 100
                this.numPages = pdfDoc.numPages
                this.pageNum = 1

                await this.$nextTick()
                this.canvas = document.getElementById(this.canvasId)
                if (!this.canvas) {
                    this.isLoading = false
                    return
                }

                this.ctx = this.canvas.getContext('2d')
                await this.renderPage(1)
            } catch (error) {
                if (loadToken === this.loadToken) {
                    this.destroyPdf()
                    this.hasError = true
                }
            } finally {
                if (loadToken === this.loadToken) {
                    this.isLoading = false
                }
            }
        },
        /**
         * Escala necesaria para que la página ocupe el ancho disponible del visor.
         *
         * @param {object} page
         * @returns {number}
         */
        getFitScale(page) {
            const container = this.$refs.pdfPages
            const unscaled = page.getViewport({ scale: 1 })

            if (!container || !unscaled.width) {
                return 1
            }

            const styles = window.getComputedStyle(container)
            const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
            const available = container.clientWidth - padding

            return available > 0 ? available / unscaled.width : 1
        },
        async renderPage(num) {
            if (!this.pdfDoc || !this.canvas) {
                return
            }

            this.pageRendering = true

            try {
                const page = await this.pdfDoc.getPage(num)
                const cssScale = this.getFitScale(page) * (this.zoom / 100)
                // Se renderiza a la densidad real de la pantalla para que el texto no se vea borroso.
                const outputScale = window.devicePixelRatio || 1
                const viewport = page.getViewport({ scale: cssScale * outputScale })

                this.canvas.width = viewport.width
                this.canvas.height = viewport.height
                this.canvas.style.width = Math.floor(viewport.width / outputScale) + 'px'
                this.canvas.style.height = Math.floor(viewport.height / outputScale) + 'px'

                await page.render({ canvasContext: this.ctx, viewport }).promise
                this.pageNum = num
            } catch (error) {
                // Render cancelado o fallido: se mantiene lo ya dibujado.
            } finally {
                this.pageRendering = false

                if (this.pageNumPending !== null) {
                    const pending = this.pageNumPending
                    this.pageNumPending = null
                    this.renderPage(pending)
                }
            }
        },
        onWindowResize() {
            clearTimeout(this.resizeTimer)
            this.resizeTimer = setTimeout(() => {
                if (this.pdfDoc) {
                    this.queueRenderPage(this.pageNum)
                }
            }, 200)
        },
        queueRenderPage(num) {
            if (this.pageRendering) {
                this.pageNumPending = num
                return
            }
            this.renderPage(num)
        },
        onPrevPage() {
            if (this.pageNum <= 1) {
                return
            }
            this.pageNum -= 1
            this.queueRenderPage(this.pageNum)
        },
        onNextPage() {
            if (!this.numPages || this.pageNum >= this.numPages) {
                return
            }
            this.pageNum += 1
            this.queueRenderPage(this.pageNum)
        },
        onZoomIn() {
            if (this.zoom >= MAX_ZOOM) {
                return
            }
            this.zoom += ZOOM_STEP
            this.queueRenderPage(this.pageNum)
        },
        onZoomOut() {
            if (this.zoom <= MIN_ZOOM) {
                return
            }
            this.zoom -= ZOOM_STEP
            this.queueRenderPage(this.pageNum)
        },
        onFullscreen() {
            const root = this.$refs.viewerRoot
            if (!root) {
                return
            }

            if (document.fullscreenElement) {
                document.exitFullscreen()
                return
            }

            if (typeof root.requestFullscreen === 'function') {
                root.requestFullscreen()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.allergens-pdf-viewer {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: #fff;
    border: 1px solid $neutro-t40;
    border-radius: 8px;
    overflow: hidden;

    .file-name {
        padding: 16px 24.5px;
        color: $main;
        font-family: $text-bold;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 18px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .preview-pdf {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .pdf-header {
        @include border-radius(3px);
        width: 100%;
        background-color: $neutro-t60;
        z-index: 1;
        padding: 10px 8px;
        overflow: hidden;
        height: fit-content;
        display: flex;
        justify-content: space-between;
        flex-shrink: 0;

        .pages,
        .zoom {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        .btn {
            @include background($color: $neutro, $size: 18px);
            width: 40px;
            height: 40px;
            display: inline-block;
            border: none;
            cursor: pointer;
            flex-shrink: 0;

            &.disabled {
                opacity: 0.4;
                pointer-events: none;
            }

            &.zoom-in {
                background-image: img('more_primary.svg');
            }

            &.zoom-out {
                background-image: img('less_primary.svg');
            }

            &.next {
                background-image: img('right_main.svg');
            }

            &.prev {
                background-image: img('left_main.svg');
            }

            &.fullscreen {
                background-color: #fff;
                background-image: img('expand_primary.svg');
                background-size: 18px;
                border: 1px solid #c4ccd9;
                border-radius: 4px;
            }
        }

        .number {
            line-height: 40px;
            color: $neutro-s50;
            font-size: 14px;

            .pdf-page-num {
                font-size: 1.2em;
                font-weight: bold;
                color: $color-black;
            }
        }
    }

    .pdf-pages {
        flex: 1;
        min-height: 0;
        overflow: auto;
        background-color: $neutro-t60;
        padding: 16px;
        // Block + text-align permite centrar la página y hacer scroll cuando se amplía.
        text-align: center;

        canvas {
            display: inline-block;
            vertical-align: top;
            background-color: #fff;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        }
    }

    .pdf-loading {
        display: block;
        width: 100%;
        height: 100%;
        min-height: 200px;
    }

    &.portrait {
        .file-name {
            text-align: left;
            padding: 16px 12px;
        }

        .pdf-header {
            justify-content: space-between;

            .zoom {
                .zoom-in,
                .zoom-out {
                    display: none;
                }
            }

            .btn {
                width: 36px;
                height: 36px;
            }

            .number {
                line-height: 36px;
            }
        }
    }
}
</style>
